import { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import {
  StepParametrosSchema,
  type StepParametrosFormValues,
} from '@/features/quotes/schemas/rd-equipamentos/step-parametros.schema';
import {
  factorInputToMicro,
  percentInputToMicro,
} from '@/features/quotes/utils/rd-units.util';
import { rdEquipamentosService } from '@/services/quotes/rd-equipamentos';
import type {
  RdParamsPayload,
  RdProposal,
} from '@/services/quotes/rd-equipamentos/types';
import type { RdQuoteStepOnNextResult } from '@/features/quotes/components/rd-equipamentos/rd-quote-step.types';
import { ValidationError } from '@/shared/exceptions/validation.error';
import { toaster } from '@/shared/ui/toaster.component';
import { refetchRdProposalAndQuote } from './refetch-rd-proposal-and-quote.util';

type FieldMetaLike = { errorMap?: Record<string, unknown> };

type SetParamsFormField = keyof StepParametrosFormValues;

/**
 * Superfície mínima de `useAppForm` (TanStack Form) usada por este hook —
 * mesma técnica de `use-set-rd-fleet.hook.ts`/`use-add-rd-equipment.hook.ts`:
 * evita tipar os ~20 parâmetros genéricos de `FormApi`.
 */
/* eslint-disable no-unused-vars -- Numa ASSINATURA de tipo os nomes dos
   parâmetros não são variáveis: documentam o contrato de quem implementa
   (é o `setFieldMeta` do TanStack Form). A regra os lê como declarações
   não usadas; o desligamento vale só para este bloco de tipos. */
type SetParamsForm = {
  state: { values: StepParametrosFormValues };
  setFieldMeta: (
    field: SetParamsFormField,
    updater: (meta: FieldMetaLike) => FieldMetaLike
  ) => void;
};
/* eslint-enable no-unused-vars */

type UseSetRdParamsParams = {
  proposalId: string;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any -- ver nota em `form` acima (`SetParamsForm`)
  form: any;
  // eslint-disable-next-line no-unused-vars -- nome do parâmetro documenta o contrato da callback
  onNext: (result?: RdQuoteStepOnNextResult) => void;
};

const FORM_FIELDS: SetParamsFormField[] = [
  'commission',
  'discountFactor',
  'installments',
  'firstInstallment',
  'paymentMethod',
];

function clearSubmitErrors(form: SetParamsForm): void {
  FORM_FIELDS.forEach(field => {
    form.setFieldMeta(field, meta => ({
      ...meta,
      errorMap: { ...meta.errorMap, onSubmit: undefined },
    }));
  });
}

function isSetParamsFormField(field: unknown): field is SetParamsFormField {
  return (
    field === 'commission' ||
    field === 'discountFactor' ||
    field === 'installments' ||
    field === 'firstInstallment' ||
    field === 'paymentMethod'
  );
}

/**
 * Orquestra o submit do Step 4 — Parâmetros (RD Equipamentos): normaliza
 * comissão/fator de desconto-agravo (display mascarado → micro, via
 * `percentInputToMicro`/`factorInputToMicro`) e parcelas (string →
 * inteiro), valida via `StepParametrosSchema`, chama `setParams` e, no
 * sucesso, refetcha `['rd-proposal', proposalId]` + `['rd-quote',
 * proposalId]` (via `refetchRdProposalAndQuote`) antes de avançar via
 * `onNext`. O backend recalcula a cotação ao salvar os parâmetros, então o
 * próximo step (Cotação) já chega precificado — sem chamada extra a
 * `calculate` — mas sem o refetch de `rd-quote` o Step Cotação mostrava o
 * prêmio anterior em cache até o usuário clicar em "Recalcular".
 *
 * Erros de negócio (400 com `detail`, sem `errors` de campo — ex.: "Acima
 * de 5 itens segue para subscrição manual.") não têm um campo de formulário
 * para apontar, então além do toast (`toaster.create`) o hook expõe
 * `submitError` para o step renderizar um `Alert.Root status='error'`
 * persistente (o toast some em 5s; o alerta fica até o próximo submit).
 */
export function useSetRdParams({
  proposalId,
  form: formRaw,
  onNext,
}: UseSetRdParamsParams) {
  const form = formRaw as SetParamsForm;
  const queryClient = useQueryClient();
  const [submitAttempted, setSubmitAttempted] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);

  const { mutateAsync, isPending } = useMutation<
    RdProposal,
    Error,
    RdParamsPayload
  >({
    mutationKey: ['quotes', 'rd-equipamentos', 'set-params', proposalId],
    mutationFn: async (payload: RdParamsPayload) => {
      const response = await rdEquipamentosService.setParams(
        proposalId,
        payload
      );
      if (!response.success || !response.data) {
        throw new Error(response.error || 'Erro ao salvar os parâmetros');
      }

      return response.data;
    },
  });

  async function handleSubmit() {
    setSubmitAttempted(true);
    setSubmitError(null);
    clearSubmitErrors(form);

    if (!proposalId) {
      toaster.create({
        type: 'error',
        title: 'Dados incompletos',
        description:
          'Proposta não encontrada. Recarregue a página e tente novamente.',
      });
      return;
    }

    const values = form.state.values;
    const parsed = StepParametrosSchema.safeParse({
      commission: percentInputToMicro(values.commission),
      discountFactor: factorInputToMicro(values.discountFactor),
      installments: Number(values.installments) || 0,
      firstInstallment: values.firstInstallment,
      // `undefined`, não `0`: "não informada" é ausência de escolha, e o
      // BFF traduz a ausência em `null` para o Motor — que é como se
      // limpa uma forma escolhida antes.
      paymentMethod: Number(values.paymentMethod) || undefined,
    });

    if (!parsed.success) {
      parsed.error.issues.forEach(issue => {
        const field = issue.path[0];
        if (!isSetParamsFormField(field)) return;
        form.setFieldMeta(field, meta => ({
          ...meta,
          errorMap: { ...meta.errorMap, onSubmit: issue.message },
        }));
      });
      return;
    }

    try {
      await mutateAsync(parsed.data);
      await refetchRdProposalAndQuote(queryClient, proposalId);
      setSubmitAttempted(false);
      onNext();
    } catch (error) {
      if (error instanceof ValidationError && error.fieldErrors) {
        Object.entries(error.fieldErrors).forEach(([field, fieldError]) => {
          if (!isSetParamsFormField(field)) return;
          form.setFieldMeta(field, meta => ({
            ...meta,
            errorMap: { ...meta.errorMap, onSubmit: fieldError.message },
          }));
        });
      }

      const description =
        error instanceof ValidationError
          ? error.detail?.trim() ||
            (error.messages.length ? error.messages.join('\n') : error.message)
          : error instanceof Error
            ? error.message
            : 'Erro inesperado';

      setSubmitError(description);

      toaster.create({
        type: 'error',
        title: 'Não foi possível salvar os parâmetros',
        description,
      });
    }
  }

  return { handleSubmit, isPending, submitAttempted, submitError };
}
